import React, { useState, useRef } from 'react';
import { X, Send, Mail, Image as ImageIcon, Link as LinkIcon, Paperclip, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { ClientAccount } from '../types';

export interface PromoCampaignPayload {
  recipients: ClientAccount[];
  subject: string;
  message: string;
  imageUrl?: string;
  imageFile?: { name: string; dataUrl: string; sizeMB: number };
  channels: { email: boolean; whatsapp: boolean };
}

export interface PromoSendResult {
  sent: number;
  failed: number;
  errors?: string[];
}

interface PromoCampaignModalProps {
  companyName?: string;
  recipients: ClientAccount[];
  onRemoveRecipient: (id: string) => void;
  onSend: (payload: PromoCampaignPayload) => Promise<PromoSendResult>;
  onClose: () => void;
}

const MAX_FILE_MB = 5;

export default function PromoCampaignModal({ companyName, recipients, onRemoveRecipient, onSend, onClose }: PromoCampaignModalProps) {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [imageFile, setImageFile] = useState<{ name: string; dataUrl: string; sizeMB: number } | null>(null);
  const [fileError, setFileError] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<PromoSendResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const sendableRecipients = recipients.filter(r => !r.marketingOptOut && r.email);
  const skippedOptOutCount = recipients.filter(r => r.marketingOptOut).length;
  const skippedNoEmailCount = recipients.filter(r => !r.marketingOptOut && !r.email).length;

  const HARD_CAP_MB = 40; // reject upfront, don't even try to decode/compress files bigger than this

  const handleFilePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileError('');
    setImageFile(null);

    const rawSizeMB = file.size / (1024 * 1024);
    if (rawSizeMB > HARD_CAP_MB) {
      setFileError(`This file is ${rawSizeMB.toFixed(1)}MB — that's too large to process. Please choose a smaller image (under ${HARD_CAP_MB}MB).`);
      return;
    }

    const img = new Image();
    const reader = new FileReader();
    reader.onload = () => {
      img.onload = () => {
        const MAX_DIM = 1280;
        let { width, height } = img;
        if (width > MAX_DIM || height > MAX_DIM) {
          const scale = MAX_DIM / Math.max(width, height);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx?.drawImage(img, 0, 0, width, height);

        // Always re-encode as JPEG, regardless of input format (png, webp, bmp, gif, heic, etc.)
        let quality = 0.8;
        let dataUrl = canvas.toDataURL('image/jpeg', quality);
        let sizeMB = (dataUrl.length * 0.75) / (1024 * 1024);

        // If still too big, step quality down a few times before giving up
        while (sizeMB > MAX_FILE_MB && quality > 0.3) {
          quality -= 0.15;
          dataUrl = canvas.toDataURL('image/jpeg', quality);
          sizeMB = (dataUrl.length * 0.75) / (1024 * 1024);
        }

        if (sizeMB > MAX_FILE_MB) {
          setFileError(`File is too big — even after compression it's ${sizeMB.toFixed(1)}MB (limit is ${MAX_FILE_MB}MB). Please try a smaller or simpler image.`);
          return;
        }

        setImageFile({
          name: file.name.replace(/\.[^.]+$/, '.jpg'),
          dataUrl,
          sizeMB: Number(sizeMB.toFixed(2)),
        });
      };
      img.onerror = () => setFileError('Could not read this file as an image. Please try a different file.');
      img.src = reader.result as string;
    };
    reader.onerror = () => setFileError('Failed to read the file. Please try again.');
    reader.readAsDataURL(file);
  };

  const canSend =
    sendableRecipients.length > 0 &&
    message.trim().length > 0 &&
    !sending;

  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    setResult(null);

    const activeBrand = companyName || 'Our Company';
    const finalSubject = subject.trim() || `Special Offer from ${activeBrand}!`;
    const finalMessage = message.trim().includes(activeBrand)
      ? message.trim()
      : `${message.trim()}\n\n— ${activeBrand}`;

    try {
      const res = await onSend({
        recipients: sendableRecipients,
        subject: finalSubject,
        message: finalMessage,
        imageUrl: imageUrl.trim() || undefined,
        imageFile: imageFile || undefined,
        channels: { email: true, whatsapp: false },
      });
      setResult(res);
    } catch (err) {
      setResult({ sent: 0, failed: sendableRecipients.length, errors: [String(err)] });
    } finally {
      setSending(false);
    }
  };

  return (
    <div 
      className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4"
      style={{ fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 sticky top-0 bg-white rounded-t-xl">
          <h2 className="text-sm font-extrabold text-slate-800 uppercase tracking-wider flex items-center gap-2">
            <Send className="w-4 h-4 text-blue-600" />
            Send Promo Campaign
          </h2>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-700 cursor-pointer">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Recipients */}
          <div>
            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
              Recipients ({recipients.length})
            </label>
            <div className="mt-1.5 flex flex-wrap gap-1.5 max-h-24 overflow-y-auto border border-slate-200 rounded-lg p-2 bg-slate-50">
              {recipients.length === 0 && (
                <span className="text-[11px] text-slate-400 italic px-1">No clients selected.</span>
              )}
              {recipients.map(r => (
                <span key={r.id} className="flex items-center gap-1 bg-white border border-slate-200 rounded-full pl-2.5 pr-1 py-1 text-[10.5px] font-semibold text-slate-700">
                  {r.name}
                  <button onClick={() => onRemoveRecipient(r.id)} className="p-0.5 hover:bg-slate-100 rounded-full cursor-pointer">
                    <X className="w-2.5 h-2.5 text-slate-400" />
                  </button>
                </span>
              ))}
            </div>
            <p className="text-[10px] text-slate-400 mt-1">
              {sendableRecipients.length} will receive this email
              {skippedOptOutCount > 0 && <span className="text-amber-600 font-semibold"> · {skippedOptOutCount} skipped (unsubscribed)</span>}
              {skippedNoEmailCount > 0 && <span className="text-amber-600 font-semibold"> · {skippedNoEmailCount} skipped (no email on file)</span>}
            </p>
          </div>

          {/* Subject */}
          <div className="space-y-1">
            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1">
              <Mail className="w-3 h-3" /> Email Subject
            </label>
            <input
              type="text"
              placeholder={`Special offer from ${companyName || 'our team'}!`}
              value={subject}
              onChange={e => setSubject(e.target.value)}
              className="w-full px-3 py-1.5 text-xs font-semibold text-slate-700 bg-slate-50 border border-slate-200 rounded focus:outline-none"
            />
          </div>

          {/* Message */}
          <div className="space-y-1">
            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Message *</label>
            <textarea
              placeholder="Write the promo message that goes out with the image..."
              value={message}
              onChange={e => setMessage(e.target.value)}
              rows={4}
              className="w-full px-3 py-1.5 text-xs font-semibold text-slate-700 bg-slate-50 border border-slate-200 rounded focus:outline-none resize-none"
            />
          </div>

          {/* Image URL */}
          <div className="space-y-1">
            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1">
              <LinkIcon className="w-3 h-3" /> Image Link (optional)
            </label>
            <input
              type="text"
              placeholder="https://drive.google.com/... or any image URL"
              value={imageUrl}
              onChange={e => setImageUrl(e.target.value)}
              className="w-full px-3 py-1.5 text-xs font-semibold text-slate-700 bg-slate-50 border border-slate-200 rounded focus:outline-none font-mono"
            />
          </div>

          {/* Image file upload */}
          <div className="space-y-1">
            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1">
              <ImageIcon className="w-3 h-3" /> Or Upload an Image (optional, max {MAX_FILE_MB}MB)
            </label>
            {!imageFile ? (
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full flex items-center justify-center gap-2 py-3 border border-dashed border-slate-300 rounded-lg text-[11px] font-bold text-slate-500 hover:bg-slate-50 cursor-pointer"
              >
                <Paperclip className="w-3.5 h-3.5" />
                Choose image
              </button>
            ) : (
              <div className="flex items-center justify-between border border-slate-200 rounded-lg px-3 py-2 bg-slate-50">
                <img src={imageFile.dataUrl} alt="preview" className="w-8 h-8 object-cover rounded" />
                <span className="text-[11px] font-semibold text-slate-700 truncate flex-1 mx-2">{imageFile.name} ({imageFile.sizeMB}MB)</span>
                <button onClick={() => setImageFile(null)} className="p-1 hover:bg-slate-100 rounded cursor-pointer">
                  <X className="w-3.5 h-3.5 text-slate-400" />
                </button>
              </div>
            )}
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFilePick} className="hidden" />
            {fileError && <p className="text-[10px] text-red-600 font-semibold">{fileError}</p>}
          </div>

          {/* Result banner */}
          {result && (
            <div className={`flex items-start gap-2 rounded-lg p-3 text-[11px] font-semibold ${result.failed === 0 ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-amber-50 text-amber-700 border border-amber-200'}`}>
              {result.failed === 0 ? <CheckCircle2 className="w-4 h-4 shrink-0" /> : <AlertTriangle className="w-4 h-4 shrink-0" />}
              <span>
                Sent to {result.sent} client{result.sent === 1 ? '' : 's'}
                {result.failed > 0 ? `, ${result.failed} failed` : ''}.
              </span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center gap-2 px-5 py-4 border-t border-slate-100 sticky bottom-0 bg-white rounded-b-xl">
          <button onClick={onClose} className="flex-1 py-2 border border-slate-200 text-slate-600 hover:bg-slate-50 rounded text-xs font-bold cursor-pointer">
            {result ? 'Close' : 'Cancel'}
          </button>
          {!result && (
            <button
              onClick={handleSend}
              disabled={!canSend}
              className="flex-1 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded text-xs font-bold flex items-center justify-center gap-1.5 cursor-pointer"
            >
              {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              {sending ? 'Sending...' : `Send to ${sendableRecipients.length}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
